export type FeedbackSurface = 'message_response' | 'general';
export type FeedbackSentiment = 'positive' | 'negative' | 'neutral';

export type FeedbackOption = {
  id: string;
  label: string;
  value: string;
};

export type FeedbackDialogCopy = {
  title: string;
  cancelLabel: string;
  submitLabel: string;
  sendLabel: string;
  closeLabel: string;
};

export type MessageFeedbackConfig = {
  question: string;
  detailsLabel: string;
  placeholder: string;
  privacyNotice: string;
  reasons: FeedbackOption[];
};

export type GeneralFeedbackConfig = {
  issueTypeLabel: string;
  issueTypePlaceholder: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  emailLabel: string;
  issueTypes: FeedbackOption[];
};

export type FeedbackConfig = {
  dialog: FeedbackDialogCopy;
  message: MessageFeedbackConfig;
  general: GeneralFeedbackConfig;
};

export type FeedbackSubmissionInput = {
  surface: FeedbackSurface;
  sentiment?: FeedbackSentiment;
  issueType?: string;
  reasonIds?: string[];
  description?: string;
  email?: string;
  conversationIncluded?: boolean;
  conversationContext?: Record<string, any>;
  screenshotUrls?: string[];
  metadata?: Record<string, any>;
};

const MESSAGE_FEEDBACK_REASONS: FeedbackOption[] = [
  {
    id: 'inaccurate_answer',
    value: 'inaccurate_answer',
    label: 'Inaccurate answer',
  },
  {
    id: 'did_not_solve_question',
    value: 'did_not_solve_question',
    label: 'Did not solve my question',
  },
  {
    id: 'too_vague',
    value: 'too_vague',
    label: 'Too vague',
  },
  {
    id: 'poor_context_continuity',
    value: 'poor_context_continuity',
    label: 'Poor context continuity',
  },
  {
    id: 'hard_to_understand',
    value: 'hard_to_understand',
    label: 'Hard to understand',
  },
  {
    id: 'slow_response',
    value: 'slow_response',
    label: 'Slow response',
  },
  {
    id: 'other',
    value: 'other',
    label: 'Other',
  },
];

const GENERAL_FEEDBACK_ISSUE_TYPES: FeedbackOption[] = [
  {
    id: 'account_or_login',
    value: 'account_or_login',
    label: 'Account or login',
  },
  {
    id: 'market_data',
    value: 'market_data',
    label: 'Market data',
  },
  {
    id: 'prediction_or_answer_quality',
    value: 'prediction_or_answer_quality',
    label: 'Prediction or answer quality',
  },
  {
    id: 'portfolio_or_wallet',
    value: 'portfolio_or_wallet',
    label: 'Portfolio or wallet',
  },
  {
    id: 'performance',
    value: 'performance',
    label: 'Performance',
  },
  {
    id: 'bug',
    value: 'bug',
    label: 'Bug',
  },
  {
    id: 'feature_request',
    value: 'feature_request',
    label: 'Feature request',
  },
  {
    id: 'other',
    value: 'other',
    label: 'Other',
  },
];

const FEEDBACK_CONFIG: FeedbackConfig = {
  dialog: {
    title: 'Feedback',
    cancelLabel: 'Cancel',
    submitLabel: 'Submit',
    sendLabel: 'Send',
    closeLabel: 'Close',
  },
  message: {
    question: "What didn't you like about this response?",
    detailsLabel: 'Tell us more（Optional）',
    placeholder: 'What was unsatisfying about this response?Please tell us more…',
    privacyNotice:
      'Your conversation will be included with your feedback to help improve Pythra.',
    reasons: MESSAGE_FEEDBACK_REASONS,
  },
  general: {
    issueTypeLabel: 'Issue type',
    issueTypePlaceholder: 'Please select an issue type',
    descriptionLabel: 'Issue description',
    descriptionPlaceholder:
      'Please tell us what happened and you can add the corresponding screenshots to help us better understand your feedback.',
    emailLabel: 'Your email',
    issueTypes: GENERAL_FEEDBACK_ISSUE_TYPES,
  },
};

export function getFeedbackConfig(): FeedbackConfig {
  return FEEDBACK_CONFIG;
}

export function getMessageFeedbackReasons(): FeedbackOption[] {
  return MESSAGE_FEEDBACK_REASONS;
}

export function getFeedbackIssueTypes(): FeedbackOption[] {
  return GENERAL_FEEDBACK_ISSUE_TYPES;
}

export function getFeedbackIssueTypeByValue(value?: string) {
  if (!value) return undefined;
  return GENERAL_FEEDBACK_ISSUE_TYPES.find((item) => item.value === value);
}

export function getMessageFeedbackReasonByValue(value?: string) {
  if (!value) return undefined;
  return MESSAGE_FEEDBACK_REASONS.find((item) => item.value === value);
}

// ── 处理侧：问题域 / 明细 / 优先级 / 状态机 / SLA（docs/prd/feedback-processing-workflow.md）──

export type FeedbackPriority = 'unrated' | 'p0' | 'p1' | 'p2' | 'p3';

export type FeedbackStatus =
  | 'open'
  | 'reviewed' // 历史遗留值，展示等同 in_progress，不再写入
  | 'in_progress'
  | 'pending_user'
  | 'resolved'
  | 'archived';

export interface FeedbackIssueDomain {
  value: string;
  label: string;
  /** 二级明细选项；空数组表示该域无二级明细 */
  details: FeedbackOption[];
}

/** 问题域词表。ai_answer_quality 的明细复用 message reasonIds 词表 */
export const FEEDBACK_ISSUE_DOMAINS: FeedbackIssueDomain[] = [
  { value: 'ai_answer_quality', label: 'AI Answer Quality', details: MESSAGE_FEEDBACK_REASONS },
  {
    value: 'product_bug',
    label: 'Product Bug',
    details: [
      { id: 'crash', value: 'crash', label: 'Crash' },
      { id: 'ui_error', value: 'ui_error', label: 'UI error' },
      { id: 'data_error', value: 'data_error', label: 'Data display error' },
      { id: 'interaction_error', value: 'interaction_error', label: 'Interaction error' },
      { id: 'other', value: 'other', label: 'Other' },
    ],
  },
  {
    value: 'account_or_login',
    label: 'Account & Login',
    details: [
      { id: 'login_failed', value: 'login_failed', label: 'Login failed' },
      { id: 'oauth_error', value: 'oauth_error', label: 'OAuth error' },
      { id: 'profile', value: 'profile', label: 'Profile issue' },
      { id: 'other', value: 'other', label: 'Other' },
    ],
  },
  {
    value: 'market_data',
    label: 'Market Data',
    details: [
      { id: 'price_error', value: 'price_error', label: 'Price error' },
      { id: 'missing_market', value: 'missing_market', label: 'Missing market' },
      { id: 'stale_data', value: 'stale_data', label: 'Stale data' },
      { id: 'other', value: 'other', label: 'Other' },
    ],
  },
  {
    value: 'portfolio_wallet',
    label: 'Portfolio & Wallet',
    details: [
      { id: 'balance_error', value: 'balance_error', label: 'Balance error' },
      { id: 'position_error', value: 'position_error', label: 'Position error' },
      { id: 'sync_failed', value: 'sync_failed', label: 'Sync failed' },
      { id: 'other', value: 'other', label: 'Other' },
    ],
  },
  {
    value: 'performance',
    label: 'Performance',
    details: [
      { id: 'slow_load', value: 'slow_load', label: 'Slow load' },
      { id: 'timeout', value: 'timeout', label: 'Timeout' },
      { id: 'freeze', value: 'freeze', label: 'Freeze' },
      { id: 'other', value: 'other', label: 'Other' },
    ],
  },
  { value: 'feature_request', label: 'Feature Request', details: [] },
  { value: 'other', label: 'Other', details: [] },
];

export function getFeedbackIssueDomainByValue(value?: string | null) {
  if (!value) return undefined;
  return FEEDBACK_ISSUE_DOMAINS.find((d) => d.value === value);
}

/** 校验 domain/detail 组合是否合法（detail 为空视为合法；无二级明细的域只接受空 detail） */
export function isValidIssueDomainDetail(domain?: string | null, detail?: string | null) {
  if (!domain) return !detail; // 无域时不允许单独设置明细
  const found = getFeedbackIssueDomainByValue(domain);
  if (!found) return false;
  if (!detail) return true;
  return found.details.some((d) => d.value === detail);
}

/** general issueType → issueDomain 映射（未列出的同名映射到 other） */
const ISSUE_TYPE_TO_DOMAIN: Record<string, string> = {
  account_or_login: 'account_or_login',
  market_data: 'market_data',
  prediction_or_answer_quality: 'ai_answer_quality',
  portfolio_or_wallet: 'portfolio_wallet',
  performance: 'performance',
  bug: 'product_bug',
  feature_request: 'feature_request',
  other: 'other',
};

/** 提交时自动推导初始 issueDomain / issueDetail（处理人可改） */
export function deriveIssueClassification(input: {
  surface: FeedbackSurface;
  issueType?: string;
  reasonIds?: string[];
}): { issueDomain: string | undefined; issueDetail: string | undefined } {
  if (input.surface === 'message_response') {
    return { issueDomain: 'ai_answer_quality', issueDetail: input.reasonIds?.[0] };
  }
  const issueDomain = input.issueType ? (ISSUE_TYPE_TO_DOMAIN[input.issueType] ?? 'other') : undefined;
  return { issueDomain, issueDetail: undefined };
}

export const FEEDBACK_PRIORITIES: FeedbackPriority[] = ['unrated', 'p0', 'p1', 'p2', 'p3'];

/** priority → SLA 时限（毫秒），unrated 无时限 */
export const FEEDBACK_SLA_MS: Record<Exclude<FeedbackPriority, 'unrated'>, number> = {
  p0: 4 * 60 * 60 * 1000,
  p1: 24 * 60 * 60 * 1000,
  p2: 3 * 24 * 60 * 60 * 1000,
  p3: 7 * 24 * 60 * 60 * 1000,
};

/** 状态转移表：key 为当前状态，值为允许转入的目标状态 */
export const FEEDBACK_STATUS_TRANSITIONS: Record<FeedbackStatus, FeedbackStatus[]> = {
  open: ['in_progress', 'pending_user', 'resolved', 'archived'],
  reviewed: ['in_progress', 'pending_user', 'resolved', 'archived', 'open'], // 历史值同 in_progress
  in_progress: ['pending_user', 'resolved', 'archived', 'open'],
  pending_user: ['in_progress', 'resolved', 'archived'],
  resolved: ['in_progress', 'archived'],
  archived: ['in_progress'],
};

export function canTransitFeedbackStatus(from: FeedbackStatus, to: FeedbackStatus) {
  if (from === to) return true;
  return FEEDBACK_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

export function normalizeFeedbackSubmission(input: FeedbackSubmissionInput) {
  return {
    surface: input.surface,
    sentiment: input.sentiment ?? 'neutral',
    issueType: input.issueType,
    reasonIds: input.reasonIds ?? [],
    description: input.description?.trim() || undefined,
    email: input.email?.trim() || undefined,
    conversationIncluded: input.conversationIncluded ?? false,
    conversationContext: input.conversationContext,
    screenshotUrls: input.screenshotUrls ?? [],
    metadata: input.metadata,
  };
}
